import { useRef, useState, useMemo } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { createPortal } from 'react-dom'
import { Calendar, ChevronDown } from 'lucide-react-native'
import { useTranslation } from 'react-i18next'
import { DayPicker, type DateRange } from 'react-day-picker'
import {
  startOfDay, endOfDay, subDays, addDays, startOfYesterday, endOfYesterday,
  format, min as dateMin,
} from 'date-fns'
import { sv } from 'date-fns/locale'
import 'react-day-picker/style.css'
import './dateRangePicker.css'
import { colors } from '@chillout/core'

type PresetKey = 'today' | 'yesterday' | 'last7Days' | 'last30Days' | 'last90Days'

const MAX_RANGE_DAYS = 90

interface Preset {
  key: PresetKey
  getRange: () => { from: Date; to: Date }
}

const PRESETS: Preset[] = [
  { key: 'today', getRange: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
  { key: 'yesterday', getRange: () => ({ from: startOfYesterday(), to: endOfYesterday() }) },
  { key: 'last7Days', getRange: () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) }) },
  { key: 'last30Days', getRange: () => ({ from: startOfDay(subDays(new Date(), 29)), to: endOfDay(new Date()) }) },
  { key: 'last90Days', getRange: () => ({ from: startOfDay(subDays(new Date(), 89)), to: endOfDay(new Date()) }) },
]

interface DateRangePickerProps {
  from: Date
  to: Date
  onChange: (from: Date, to: Date) => void
  disabled?: boolean
}

interface PanelPosition {
  top: number
  right: number
}

function getMatchingPreset(from: Date, to: Date): PresetKey | null {
  for (const preset of PRESETS) {
    const range = preset.getRange()
    if (
      startOfDay(from).getTime() === startOfDay(range.from).getTime() &&
      startOfDay(to).getTime() === startOfDay(range.to).getTime()
    ) {
      return preset.key
    }
  }
  return null
}

export function DateRangePicker({ from, to, onChange, disabled = false }: DateRangePickerProps) {
  const { t, i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [panelPos, setPanelPos] = useState<PanelPosition | null>(null)
  const [calendarMonth, setCalendarMonth] = useState(from)
  const buttonRef = useRef<View>(null)

  const today = useMemo(() => startOfDay(new Date()), [])
  const activePreset = useMemo(() => getMatchingPreset(from, to), [from, to])
  const locale = i18n.language === 'sv' ? sv : undefined

  const triggerLabel = activePreset
    ? t(`auditLog.dateRange.${activePreset}`)
    : `${format(from, 'd MMM', { locale })} – ${format(to, 'd MMM', { locale })}`

  function handleToggle() {
    if (disabled) return
    if (!isOpen && buttonRef.current) {
      const el = buttonRef.current as unknown as HTMLElement
      const rect = el.getBoundingClientRect()
      const right = window.innerWidth - rect.right
      setPanelPos({ top: rect.bottom + 4, right })
      setCalendarMonth(from)
    }
    setIsOpen((v) => !v)
  }

  function handlePresetPress(preset: Preset) {
    const range = preset.getRange()
    onChange(range.from, range.to)
    setCalendarMonth(range.to)
  }

  function handleCalendarSelect(range: DateRange | undefined) {
    if (!range?.from) return

    if (range.to) {
      const maxEnd = dateMin([addDays(range.from, MAX_RANGE_DAYS - 1), today])
      const clampedTo = dateMin([range.to, maxEnd])
      onChange(startOfDay(range.from), endOfDay(clampedTo))
    } else {
      onChange(startOfDay(range.from), endOfDay(range.from))
    }
  }

  const disabledDays = useMemo(() => ({ after: today }), [today])

  const portal =
    isOpen && panelPos
      ? createPortal(
          <>
            <Pressable
              className="fixed inset-0"
              style={{ zIndex: 9998 }}
              onPress={() => setIsOpen(false)}
              accessibilityLabel={t('auditLog.dateRange.closeAccessibilityLabel')}
            />
            <ScrollView
              className="fixed rounded-lg border border-border bg-canvas-card"
              contentContainerClassName="items-center lg:items-stretch lg:flex-row"
              style={{
                top: panelPos.top,
                ...(window.innerWidth < 480
                  ? { left: 8, right: 8 }
                  : { right: panelPos.right }),
                zIndex: 9999,
                maxWidth: `calc(100vw - 16px)`,
                maxHeight: `calc(100vh - ${panelPos.top + 8}px)`,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.08,
                shadowRadius: 12,
              } as object}
              bounces={false}
            >
              <View className="p-3">
                <DayPicker
                  mode="range"
                  selected={{ from: startOfDay(from), to: startOfDay(to) }}
                  onSelect={handleCalendarSelect}
                  month={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  disabled={disabledDays}
                  locale={locale}
                  style={{
                    // @ts-expect-error -- CSS custom properties for react-day-picker theming
                    '--rdp-accent-color': colors.accent.DEFAULT,
                    '--rdp-accent-background-color': colors.accent.surface,
                    fontSize: 14,
                  }}
                />
              </View>
              <View className="w-full border-t border-border py-2 lg:w-auto lg:border-l lg:border-t-0" style={{ minWidth: 160 }}>
                {PRESETS.map((preset) => {
                  const isActive = activePreset === preset.key
                  return (
                    <Pressable
                      key={preset.key}
                      onPress={() => handlePresetPress(preset)}
                      accessibilityRole="button"
                      accessibilityState={{ selected: isActive }}
                      accessibilityLabel={t(`auditLog.dateRange.${preset.key}`)}
                      className={`px-4 py-2.5 ${isActive ? 'bg-accent/5' : ''}`}
                    >
                      <Text
                        className={`text-sm ${isActive ? 'font-semibold text-accent' : 'font-normal text-primary'}`}
                      >
                        {t(`auditLog.dateRange.${preset.key}`)}
                      </Text>
                    </Pressable>
                  )
                })}
              </View>
            </ScrollView>
          </>,
          document.body,
        )
      : null

  return (
    <View>
      <Pressable
        ref={buttonRef}
        onPress={handleToggle}
        accessibilityRole="button"
        accessibilityLabel={triggerLabel}
        accessibilityState={{ disabled, expanded: isOpen }}
        className={`flex-row items-center gap-1.5 rounded-md border border-border bg-canvas-card px-3 py-2 ${
          disabled ? 'opacity-50' : ''
        }`}
      >
        <Calendar size={14} color={colors.text.secondary} strokeWidth={2} />
        <Text className="text-sm font-medium text-primary">{triggerLabel}</Text>
        <View style={{ transform: [{ rotate: isOpen ? '180deg' : '0deg' }] }}>
          <ChevronDown size={14} color={colors.text.secondary} strokeWidth={2} />
        </View>
      </Pressable>
      {portal}
    </View>
  )
}